// GFM task-list helpers (tick boxes note #314).
//
// A note's storage is and stays MARKDOWN, so a checkbox is `- [ ]` / `- [x]` in
// the body — nothing new is persisted, and a ticked box survives every surface
// that already renders note markdown (react-markdown + remark-gfm parses task
// lists natively).
//
// Toggling therefore means rewriting the Nth task marker in the SOURCE. The
// index is the one thing that has to be exactly right: the rendered view counts
// its checkbox inputs in DOM order, and remark-gfm emits one input per task
// item in document order, so counting task markers here in the same order makes
// the two agree — including across nested lists and content interleaved between
// them.

// A task item is a list marker (-, * or +), then a `[ ]`/`[x]` box, then a
// space or end of line. Leading indentation is captured so nesting is preserved
// verbatim on write-back, and the box must be followed by whitespace/EOL so a
// line like `- [link](url)` is never mistaken for one.
const TASK_RE = /^(\s*(?:[-*+]|\d+[.)])\s+)\[([ xX])\](\s|$)/;

/** Is this source line a GFM task-list item? */
export const isTaskLine = (line) => TASK_RE.test(line || '');

/** Every task item in document order: `[{ line, checked }]`. */
export function taskItems(markdown) {
    const out = [];
    String(markdown || '').split('\n').forEach((line, i) => {
        const m = TASK_RE.exec(line);
        if (m) out.push({ line: i, checked: m[2] !== ' ' });
    });
    return out;
}

/** How many checkboxes the rendered view should show. */
export const countTasks = (markdown) => taskItems(markdown).length;

/**
 * Flip the `index`-th task marker (0-based, document order).
 *
 * `checked` forces a state instead of toggling — the rendered input already
 * knows what it just became, so passing it through avoids any disagreement
 * between the DOM and the source. Out-of-range indexes return the markdown
 * unchanged: a stale click must never rewrite an unrelated line.
 */
export function toggleTaskAtIndex(markdown, index, checked) {
    const src = String(markdown || '');
    const items = taskItems(src);
    const target = items[index];
    if (!target) return src;

    const lines = src.split('\n');
    const line = lines[target.line];
    const m = TASK_RE.exec(line);
    if (!m) return src;

    const next = checked === undefined ? !target.checked : !!checked;
    lines[target.line] = `${m[1]}[${next ? 'x' : ' '}]${line.slice(m[1].length + 3)}`;
    return lines.join('\n');
}
